import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "inventory-view" }
const _hoisted_2 = { class: "text-body-1 mb-4" }
const _hoisted_3 = { class: "text-body-1 mb-4" }
const _hoisted_4 = { class: "text-body-1 mb-4" }
const _hoisted_5 = { class: "text-body-1 mb-4" }

import { ref, computed, watch, onMounted } from 'vue'
import { api } from '@/composables/api'
import { useAuthStore } from '@/store/auth'
import InventoryFilters from '@/components/inventory/InventoryFilters.vue'
import BulkActionsBar from '@/components/inventory/BulkActionsBar.vue'
import InventoryTable from '@/components/inventory/InventoryTable.vue'
import EquipmentModal from '@/components/inventory/EquipmentModal.vue'
import BulkEditModal from '@/components/inventory/BulkEditModal.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'InventoryView',
  setup(__props) {

const authStore = useAuthStore()
const loading = ref(true)
const equipment = ref<any[]>([])
const searchQuery = ref('')
const filterType = ref('')
const filterStatus = ref<string[]>(['processing', 'available'])

// Selection state
const selectedItems = ref<string[]>([])

// Modal state
const showModal = ref(false)
const isEditing = ref(false)
const editingId = ref<string | null>(null)
const submitting = ref(false)
const error = ref('')

// Claim by name modal state
const showClaimByNameModal = ref(false)
const equipmentToClaim = ref<any>(null)
const claimedForName = ref('')
const claimError = ref('')

// Bulk edit modal state
const showBulkEditModal = ref(false)
const bulkEditEquipmentIds = ref<string[]>([])
const bulkEditSubmitting = ref(false)
const bulkEditError = ref('')

// Add more of model modal state
const showAddMoreModal = ref(false)
const equipmentToClone = ref<any>(null)
const addMoreQuantity = ref(1)
const addMoreStatus = ref('processing')
const addMoreLocation = ref('')
const addMoreValue = ref<number | null>(null)
const addMoreDonor = ref('')
const addMoreDonatedOn = ref('')
const addMoreCopyDonor = ref(false)
const addMoreSubmitting = ref(false)
const addMoreError = ref('')

// Donation receipt modal state
const showReceiptModal = ref(false)
const receiptEquipmentIds = ref<string[]>([])
const receiptGroupByDonor = ref(true)
const receiptLoading = ref(false)

// Print format modal state
const showPrintFormatModal = ref(false)
const printEquipmentIds = ref<string[]>([])
const selectedPrintFormat = ref('slips')
const printLoading = ref(false)

// Check if user is admin
const isAdmin = computed(() => {
  return authStore.authUser && authStore.authUser.isAdmin()
})

// Form data
const formData = ref({
  equipment_type: 'laptop',
  quantity: 1,
  manufacturer: '',
  model: '',
  rating: null as number | null,
  screen: '',
  cpu: '',
  ram: '',
  storage: '',
  gpu: '',
  os: '',
  charger: '',
  status: 'available',
  location: '',
  asset_tag: '',
  comments: '',
  value: null as number | null,
  donor: '',
  donated_on: ''
})

const filteredEquipment = computed(() => {
  let filtered = equipment.value

  // Apply search
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(item =>
      (item.equipment_type?.toLowerCase() || '').includes(query) ||
      (item.manufacturer?.toLowerCase() || '').includes(query) ||
      (item.model?.toLowerCase() || '').includes(query) ||
      (item.location?.toLowerCase() || '').includes(query) ||
      (item.asset_tag?.toLowerCase() || '').includes(query) ||
      (item.donor?.toLowerCase() || '').includes(query)
    )
  }

  // Apply type filter
  if (filterType.value) {
    filtered = filtered.filter(item => item.equipment_type === filterType.value)
  }

  // Apply status filter
  if (filterStatus.value && filterStatus.value.length > 0) {
    filtered = filtered.filter(item => filterStatus.value.includes(item.status))
  }

  return filtered
})


const printSlips = () => {
  alert(`Printing slips for ${selectedItems.value.length} items`)
  // TODO: Implement print slip functionality
}

const markAsDelivered = () => {
  const location = prompt('Enter delivery location:')
  if (location) {
    alert(`Marking ${selectedItems.value.length} items as delivered to ${location}`)
    // TODO: Implement mark as delivered functionality
    selectedItems.value = []
  }
}

// Convert an API datetime (ISO string) into a value the <input type="date"> accepts
const toDateInput = (value?: string | null) => {
  if (!value) return ''
  return String(value).slice(0, 10)
}

// An emptied number input yields '' rather than null, which the API cannot parse as a float
const normalizeValue = (value: any): number | null => {
  if (value === '' || value === null || value === undefined || Number.isNaN(value)) {
    return null
  }
  return Number(value)
}

// Strip empty optional fields so the API does not try to parse '' as a number/date
const buildEquipmentPayload = (data: any) => {
  const payload = { ...data }
  delete payload.quantity

  payload.value = normalizeValue(payload.value)
  if (!payload.donated_on) {
    delete payload.donated_on
  }
  payload.donor = (payload.donor || '').trim()
  payload.charger = (payload.charger || '').trim()

  return payload
}

const loadEquipment = async () => {
  loading.value = true
  try {
    const response = await api.get('/equipment')
    if (response.data) {
      equipment.value = response.data
    }
  } catch (error) {
    console.error('Error loading equipment:', error)
  } finally {
    loading.value = false
  }
}

const openAddModal = () => {
  isEditing.value = false
  editingId.value = null
  formData.value = {
    equipment_type: 'laptop',
    quantity: 1,
    manufacturer: '',
    model: '',
    rating: null,
    screen: '',
    cpu: '',
    ram: '',
    storage: '',
    gpu: '',
    os: '',
    charger: '',
    status: 'available',
    location: '',
    asset_tag: '',
    comments: '',
    value: null,
    donor: '',
    donated_on: ''
  }
  showModal.value = true
  error.value = ''
}

const openEditModal = (item: any) => {
  isEditing.value = true
  editingId.value = item.id
  formData.value = {
    equipment_type: item.equipment_type || '',
    quantity: 1,
    manufacturer: item.manufacturer || '',
    model: item.model || '',
    rating: item.rating || null,
    screen: item.screen || '',
    cpu: item.cpu || '',
    ram: item.ram || '',
    storage: item.storage || '',
    gpu: item.gpu || '',
    os: item.os || '',
    charger: item.charger || '',
    status: item.status || 'available',
    location: item.location || '',
    asset_tag: item.asset_tag || '',
    comments: item.comments || '',
    value: item.value ?? null,
    donor: item.donor || '',
    donated_on: toDateInput(item.donated_on)
  }
  showModal.value = true
  error.value = ''
}

const closeModal = () => {
  showModal.value = false
  formData.value = {
    equipment_type: 'laptop',
    quantity: 1,
    manufacturer: '',
    model: '',
    rating: null,
    screen: '',
    cpu: '',
    ram: '',
    storage: '',
    gpu: '',
    os: '',
    charger: '',
    status: 'available',
    location: '',
    asset_tag: '',
    comments: '',
    value: null,
    donor: '',
    donated_on: ''
  }
  error.value = ''
}

const submitEquipment = async (data: any) => {
  submitting.value = true
  error.value = ''

  try {
    if (isEditing.value && editingId.value) {
      // Update existing equipment
      const equipmentData = buildEquipmentPayload(data)
      await api.put(`/equipment/${editingId.value}`, equipmentData)
    } else {
      // Add new equipment
      const quantity = data.quantity
      const equipmentData = buildEquipmentPayload(data)

      // Add multiple items if quantity > 1
      for (let i = 0; i < quantity; i++) {
        await api.post('/equipment', equipmentData)
      }
    }

    await loadEquipment()
    closeModal()
  } catch (error: any) {
    console.error('Error saving equipment:', error)
    if (error.response?.status === 403) {
      error.value = 'You do not have permission to perform this action.'
    } else {
      error.value = 'Failed to save equipment. Please try again.'
    }
  } finally {
    submitting.value = false
  }
}

const deleteEquipment = async (item: any) => {
  if (confirm(`Are you sure you want to delete this ${item.equipment_type}?`)) {
    try {
      await api.delete(`/equipment/${item.id}`)
      await loadEquipment()
    } catch (error: any) {
      console.error('Error deleting equipment:', error)
      if (error.response?.status === 403) {
        alert('You do not have permission to delete equipment.')
      } else {
        alert('Failed to delete equipment')
      }
    }
  }
}

const openClaimByNameModal = (item: any) => {
  equipmentToClaim.value = item
  claimedForName.value = ''
  claimError.value = ''
  showClaimByNameModal.value = true
}

const closeClaimByNameModal = () => {
  showClaimByNameModal.value = false
  equipmentToClaim.value = null
  claimedForName.value = ''
  claimError.value = ''
}

const confirmClaimByName = async () => {
  if (!equipmentToClaim.value || !claimedForName.value.trim()) {
    claimError.value = 'Please enter a recipient name'
    return
  }

  try {
    await api.patch(`/equipment/${equipmentToClaim.value.id}/claim?claimed_for=${encodeURIComponent(claimedForName.value.trim())}`)
    await loadEquipment()
    closeClaimByNameModal()
  } catch (error: any) {
    console.error('Error claiming equipment:', error)
    claimError.value = 'Failed to claim equipment. Please try again.'
  }
}

// Print packing slips function - opens format selection modal
const printPackingSlips = (equipmentIds: string[]) => {
  printEquipmentIds.value = equipmentIds
  selectedPrintFormat.value = 'slips'
  showPrintFormatModal.value = true
}

const closePrintFormatModal = () => {
  showPrintFormatModal.value = false
  printEquipmentIds.value = []
  selectedPrintFormat.value = 'slips'
}

const confirmPrint = async () => {
  if (!printEquipmentIds.value.length) {
    return
  }

  printLoading.value = true

  try {
    // Call the packing slip API endpoint with format selection
    const response = await api.post('/equipment/packing-slips', {
      equipment_ids: printEquipmentIds.value,
      template_type: selectedPrintFormat.value
    })

    // Open the HTML in a new window for printing
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(response.data)
      printWindow.document.close()
      // Optionally trigger print dialog
      setTimeout(() => {
        printWindow.focus()
        // printWindow.print() // Uncomment to auto-open print dialog
      }, 250)
    } else {
      alert('Please allow popups for this site to print packing slips.')
    }

    closePrintFormatModal()
  } catch (error: any) {
    console.error('Error generating packing slips:', error)
    alert('Failed to generate packing slips. Please try again.')
  } finally {
    printLoading.value = false
  }
}

// Bulk edit functions
const openBulkEditModal = (equipmentIds: string[]) => {
  bulkEditEquipmentIds.value = equipmentIds
  bulkEditError.value = ''
  showBulkEditModal.value = true
}

const closeBulkEditModal = () => {
  showBulkEditModal.value = false
  bulkEditEquipmentIds.value = []
  bulkEditError.value = ''
}

const confirmBulkEdit = async (updateData: any) => {
  if (!bulkEditEquipmentIds.value.length) {
    bulkEditError.value = 'No equipment selected'
    return
  }

  // Check if any fields were filled in
  if (Object.keys(updateData).length === 0) {
    bulkEditError.value = 'Please fill in at least one field to update'
    return
  }

  bulkEditSubmitting.value = true
  bulkEditError.value = ''

  try {
    // Call bulk update API
    await api.patch('/equipment/bulk', {
      equipment_ids: bulkEditEquipmentIds.value,
      update_data: updateData
    })

    await loadEquipment()
    selectedItems.value = []
    closeBulkEditModal()
  } catch (error: any) {
    console.error('Error bulk updating equipment:', error)
    if (error.response?.status === 403) {
      bulkEditError.value = 'You do not have permission to perform bulk updates.'
    } else {
      bulkEditError.value = error.response?.data?.detail || 'Failed to update equipment. Please try again.'
    }
  } finally {
    bulkEditSubmitting.value = false
  }
}

// Add more of model functions
const openAddMoreModal = (item: any) => {
  equipmentToClone.value = item
  addMoreQuantity.value = 1
  addMoreStatus.value = 'processing'
  addMoreLocation.value = item.location || ''
  // Value carries over with the model, the donor does not unless it is specified here
  addMoreValue.value = item.value ?? null
  addMoreDonor.value = ''
  addMoreDonatedOn.value = ''
  addMoreCopyDonor.value = false
  addMoreError.value = ''
  showAddMoreModal.value = true
}

const closeAddMoreModal = () => {
  showAddMoreModal.value = false
  equipmentToClone.value = null
  addMoreQuantity.value = 1
  addMoreStatus.value = 'processing'
  addMoreLocation.value = ''
  addMoreValue.value = null
  addMoreDonor.value = ''
  addMoreDonatedOn.value = ''
  addMoreCopyDonor.value = false
  addMoreError.value = ''
}

// Ticking "same donor as original" fills the donor fields from the source item
watch(addMoreCopyDonor, (copy) => {
  if (copy && equipmentToClone.value) {
    addMoreDonor.value = equipmentToClone.value.donor || ''
    addMoreDonatedOn.value = toDateInput(equipmentToClone.value.donated_on)
  } else if (!copy) {
    addMoreDonor.value = ''
    addMoreDonatedOn.value = ''
  }
})

const confirmAddMore = async () => {
  if (!equipmentToClone.value || !addMoreQuantity.value || addMoreQuantity.value < 1) {
    addMoreError.value = 'Please enter a valid quantity'
    return
  }

  if (addMoreQuantity.value > 50) {
    addMoreError.value = 'Maximum quantity is 50 items at a time'
    return
  }

  addMoreSubmitting.value = true
  addMoreError.value = ''

  try {
    // Create equipment data by copying specs
    // Exclude status-based fields: claimed_by, claimed_for, claimed_on, distributed_on, distributed_by
    // Also exclude unique identifiers like id, asset_tag
    const equipmentData = {
      equipment_type: equipmentToClone.value.equipment_type,
      manufacturer: equipmentToClone.value.manufacturer,
      model: equipmentToClone.value.model,
      rating: equipmentToClone.value.rating,
      screen: equipmentToClone.value.screen,
      cpu: equipmentToClone.value.cpu,
      ram: equipmentToClone.value.ram,
      storage: equipmentToClone.value.storage,
      gpu: equipmentToClone.value.gpu,
      os: equipmentToClone.value.os,
      charger: equipmentToClone.value.charger,
      status: addMoreStatus.value, // Use the selected status from the form
      location: addMoreLocation.value, // Use the location from the form
      comments: equipmentToClone.value.comments,
      value: normalizeValue(addMoreValue.value), // Value copies from the original by default
      donor: addMoreDonor.value.trim(), // Donor only carries over when specified in the form
      donated_on: addMoreDonatedOn.value || undefined
      // Explicitly NOT copying: claimed_by, claimed_for, claimed_on, distributed_on, distributed_by, asset_tag, id
    }

    if (!equipmentData.donated_on) {
      delete equipmentData.donated_on
    }

    // Add multiple items
    for (let i = 0; i < addMoreQuantity.value; i++) {
      await api.post('/equipment', equipmentData)
    }

    await loadEquipment()
    closeAddMoreModal()
  } catch (error: any) {
    console.error('Error adding equipment:', error)
    if (error.response?.status === 403) {
      addMoreError.value = 'You do not have permission to add equipment.'
    } else {
      addMoreError.value = 'Failed to add equipment. Please try again.'
    }
  } finally {
    addMoreSubmitting.value = false
  }
}

// Donation receipt printing - a single item or a batch from the list
const printDonationReceipts = (equipmentIds: string[]) => {
  receiptEquipmentIds.value = equipmentIds
  receiptGroupByDonor.value = true
  showReceiptModal.value = true
}

const closeReceiptModal = () => {
  showReceiptModal.value = false
  receiptEquipmentIds.value = []
  receiptGroupByDonor.value = true
}

const receiptItemsMissingDonor = computed(() => {
  return equipment.value.filter(
    item => receiptEquipmentIds.value.includes(item.id) && !item.donor
  )
})

const confirmPrintReceipts = async () => {
  if (!receiptEquipmentIds.value.length) {
    return
  }

  receiptLoading.value = true

  try {
    const response = await api.post('/equipment/donation-receipts', {
      equipment_ids: receiptEquipmentIds.value,
      group_by_donor: receiptGroupByDonor.value
    })

    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(response.data)
      printWindow.document.close()
      setTimeout(() => printWindow.focus(), 250)
    } else {
      alert('Please allow popups for this site to print donation receipts.')
    }

    closeReceiptModal()
  } catch (error: any) {
    console.error('Error generating donation receipts:', error)
    alert('Failed to generate donation receipts. Please try again.')
  } finally {
    receiptLoading.value = false
  }
}

onMounted(() => {
  loadEquipment()
})

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_radio = _resolveComponent("v-radio")!
  const _component_v_radio_group = _resolveComponent("v-radio-group")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", { class: "page-header" }, [
      _cache[18] || (_cache[18] = _createElementVNode("div", null, [
        _createElementVNode("h1", null, "Equipment Inventory"),
        _createElementVNode("p", null, "Manage and track equipment inventory")
      ], -1)),
      _createElementVNode("div", { class: "header-actions" }, [
        _createElementVNode("button", {
          onClick: openAddModal,
          class: "btn-add"
        }, "+ Add Equipment")
      ])
    ]),
    _createVNode(InventoryFilters, {
      search: searchQuery.value,
      type: filterType.value,
      status: filterStatus.value,
      "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => (searchQuery.value = $event)),
      "onUpdate:type": _cache[1] || (_cache[1] = ($event: any) => (filterType.value = $event)),
      "onUpdate:status": _cache[2] || (_cache[2] = ($event: any) => (filterStatus.value = $event))
    }, null, 8, ["search", "type", "status"]),
    _createVNode(InventoryTable, {
      equipment: filteredEquipment.value,
      loading: loading.value,
      "is-admin": isAdmin.value,
      "selected-items": selectedItems.value,
      "onUpdate:selectedItems": _cache[3] || (_cache[3] = ($event: any) => (selectedItems.value = $event)),
      onEdit: openEditModal,
      onDelete: deleteEquipment,
      onClaim: openClaimByNameModal,
      onBulkEdit: openBulkEditModal,
      onPrintPackingSlips: printPackingSlips,
      onPrintDonationReceipts: printDonationReceipts,
      onAddMoreOfModel: openAddMoreModal
    }, null, 8, ["equipment", "loading", "is-admin", "selected-items"]),
    _createVNode(EquipmentModal, {
      show: showModal.value,
      "is-editing": isEditing.value,
      "form-data": formData.value,
      submitting: submitting.value,
      error: error.value,
      onClose: closeModal,
      onSubmit: submitEquipment
    }, null, 8, ["show", "is-editing", "form-data", "submitting", "error"]),
    _createVNode(BulkEditModal, {
      show: showBulkEditModal.value,
      "item-count": bulkEditEquipmentIds.value.length,
      submitting: bulkEditSubmitting.value,
      error: bulkEditError.value,
      onClose: closeBulkEditModal,
      onSubmit: confirmBulkEdit
    }, null, 8, ["show", "item-count", "submitting", "error"]),
    _createVNode(_component_v_dialog, {
      modelValue: showClaimByNameModal.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showClaimByNameModal).value = $event)),
      "max-width": "500px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, null, {
              default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                _createElementVNode("h2", null, "Claim Equipment", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_2, [
                  _cache[20] || (_cache[20] = _createTextVNode(" Claim ", -1)),
                  _createElementVNode("strong", null, _toDisplayString(equipmentToClaim.value?.manufacturer) + " " + _toDisplayString(equipmentToClaim.value?.model), 1)
                ]),
                _createVNode(_component_v_text_field, {
                  modelValue: claimedForName.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((claimedForName).value = $event)),
                  label: "Recipient Name",
                  placeholder: "Enter student or teacher name",
                  variant: "outlined",
                  autofocus: "",
                  "error-messages": claimError.value
                }, null, 8, ["modelValue", "error-messages"]),
                _createVNode(_component_v_alert, {
                  type: "info",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                    _createTextVNode(" This will mark the equipment as claimed for the specified person. ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  onClick: closeClaimByNameModal
                }, {
                  default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "success",
                  onClick: confirmClaimByName,
                  disabled: !claimedForName.value || claimedForName.value.trim() === ''
                }, {
                  default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                    _createTextVNode(" Claim ", -1)
                  ]))]),
                  _: 1
                }, 8, ["disabled"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: showPrintFormatModal.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((showPrintFormatModal).value = $event)),
      "max-width": "500px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, null, {
              default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                _createElementVNode("h2", null, "Select Print Format", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_3, " Choose a print format for " + _toDisplayString(printEquipmentIds.value.length) + " item(s): ", 1),
                _createVNode(_component_v_radio_group, {
                  modelValue: selectedPrintFormat.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((selectedPrintFormat).value = $event)),
                  class: "mt-2"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_radio, { value: "slips" }, {
                      label: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                        _createElementVNode("div", null, [
                          _createElementVNode("div", { class: "font-weight-medium" }, "Packing Slips"),
                          _createElementVNode("div", { class: "text-caption text-grey" }, "Full-page slips (3 per 8.5\" x 11\" page) - detailed info cards")
                        ], -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_component_v_radio, {
                      value: "labels",
                      class: "mt-3"
                    }, {
                      label: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                        _createElementVNode("div", null, [
                          _createElementVNode("div", { class: "font-weight-medium" }, "Adhesive Labels"),
                          _createElementVNode("div", { class: "text-caption text-grey" }, "4\" x 3.33\" labels (6 per 8.5\" x 11\" sheet) - for label paper")
                        ], -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  onClick: closePrintFormatModal
                }, {
                  default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "primary",
                  onClick: confirmPrint,
                  loading: printLoading.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { start: "" }, {
                      default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                        _createTextVNode("mdi-printer", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[29] || (_cache[29] = _createTextVNode(" Print ", -1))
                  ]),
                  _: 1
                }, 8, ["loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: showReceiptModal.value,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((showReceiptModal).value = $event)),
      "max-width": "560px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, null, {
              default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                _createElementVNode("h2", null, "Print Donation Receipt", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_4, " Generate a receipt for " + _toDisplayString(receiptEquipmentIds.value.length) + " item(s). ", 1),
                _createVNode(_component_v_radio_group, {
                  modelValue: receiptGroupByDonor.value,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((receiptGroupByDonor).value = $event)),
                  class: "mt-2"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_radio, { value: true }, {
                      label: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                        _createElementVNode("div", null, [
                          _createElementVNode("div", { class: "font-weight-medium" }, "One receipt per donor"),
                          _createElementVNode("div", { class: "text-caption text-grey" }, " Items are grouped by donor, each listed with its own donation date and value ")
                        ], -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_component_v_radio, {
                      value: false,
                      class: "mt-3"
                    }, {
                      label: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                        _createElementVNode("div", null, [
                          _createElementVNode("div", { class: "font-weight-medium" }, "One receipt per item"),
                          _createElementVNode("div", { class: "text-caption text-grey" }, "A separate receipt page for every selected item")
                        ], -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                (receiptItemsMissingDonor.value.length)
                  ? (_openBlock(), _createBlock(_component_v_alert, {
                      key: 0,
                      type: "warning",
                      variant: "tonal",
                      class: "mt-2"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(receiptItemsMissingDonor.value.length) + " of the selected item(s) have no donor recorded. They will print under \"Donor not recorded\". ", 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  onClick: closeReceiptModal
                }, {
                  default: _withCtx(() => [...(_cache[33] || (_cache[33] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "primary",
                  onClick: confirmPrintReceipts,
                  loading: receiptLoading.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { start: "" }, {
                      default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                        _createTextVNode("mdi-receipt-text-outline", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[35] || (_cache[35] = _createTextVNode(" Print ", -1))
                  ]),
                  _: 1
                }, 8, ["loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: showAddMoreModal.value,
      "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((showAddMoreModal).value = $event)),
      "max-width": "600px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, null, {
              default: _withCtx(() => [...(_cache[36] || (_cache[36] = [
                _createElementVNode("h2", null, "Add More of This Model", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_5, [
                  _cache[37] || (_cache[37] = _createTextVNode(" Add more ", -1)),
                  _createElementVNode("strong", null, _toDisplayString(equipmentToClone.value?.manufacturer) + " " + _toDisplayString(equipmentToClone.value?.model), 1)
                ]),
                _createVNode(_component_v_text_field, {
                  modelValue: addMoreQuantity.value,
                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((addMoreQuantity).value = $event)),
                  modelModifiers: { number: true },
                  label: "Quantity",
                  type: "number",
                  min: "1",
                  max: "50",
                  variant: "outlined",
                  class: "mb-3"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_select, {
                  modelValue: addMoreStatus.value,
                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((addMoreStatus).value = $event)),
                  label: "Status",
                  items: [
              { title: 'Available', value: 'available' },
              { title: 'Processing', value: 'processing' },
              { title: 'Troubleshooting', value: 'troubleshooting' },
              { title: 'Parts', value: 'parts' },
              { title: 'Claimed', value: 'claimed' },
              { title: 'Distributed', value: 'distributed' }
            ],
                  variant: "outlined",
                  class: "mb-3"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_text_field, {
                  modelValue: addMoreLocation.value,
                  "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((addMoreLocation).value = $event)),
                  label: "Location",
                  placeholder: "e.g., Warehouse A, Shelf 3",
                  variant: "outlined",
                  class: "mb-3"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_text_field, {
                  modelValue: addMoreValue.value,
                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((addMoreValue).value = $event)),
                  modelModifiers: { number: true },
                  label: "Value ($)",
                  type: "number",
                  min: "0",
                  step: "0.01",
                  prefix: "$",
                  variant: "outlined",
                  class: "mb-3",
                  hint: "Copied from the original item - change it here if these are worth something different",
                  "persistent-hint": ""
                }, null, 8, ["modelValue"]),
                (equipmentToClone.value?.donor)
                  ? (_openBlock(), _createBlock(_component_v_checkbox, {
                      key: 0,
                      modelValue: addMoreCopyDonor.value,
                      "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((addMoreCopyDonor).value = $event)),
                      label: `Same donor as original (${equipmentToClone.value?.donor})`,
                      density: "compact",
                      "hide-details": "",
                      class: "mb-2"
                    }, null, 8, ["modelValue", "label"]))
                  : _createCommentVNode("", true),
                _createVNode(_component_v_text_field, {
                  modelValue: addMoreDonor.value,
                  "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((addMoreDonor).value = $event)),
                  label: "Donor",
                  placeholder: "Leave blank if these were not donated by anyone recorded",
                  variant: "outlined",
                  class: "mb-3",
                  hint: "The donor is not copied unless you specify it here",
                  "persistent-hint": ""
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_text_field, {
                  modelValue: addMoreDonatedOn.value,
                  "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((addMoreDonatedOn).value = $event)),
                  label: "Donation Date",
                  type: "date",
                  variant: "outlined",
                  class: "mb-3"
                }, null, 8, ["modelValue"]),
                _createVNode(_component_v_alert, {
                  type: "info",
                  variant: "tonal",
                  class: "mb-3"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" This will create " + _toDisplayString(addMoreQuantity.value || 0) + " new equipment item(s) with the following specifications: ", 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card, {
                  variant: "outlined",
                  class: "pa-3"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_row, { dense: "" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, { cols: "6" }, {
                          default: _withCtx(() => [
                            _cache[38] || (_cache[38] = _createElementVNode("div", { class: "text-caption text-grey" }, "Type", -1)),
                            _createElementVNode("div", null, _toDisplayString(equipmentToClone.value?.equipment_type), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, { cols: "6" }, {
                          default: _withCtx(() => [
                            _cache[39] || (_cache[39] = _createElementVNode("div", { class: "text-caption text-grey" }, "Rating", -1)),
                            _createElementVNode("div", null, _toDisplayString(equipmentToClone.value?.rating || 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, { cols: "6" }, {
                          default: _withCtx(() => [
                            _cache[40] || (_cache[40] = _createElementVNode("div", { class: "text-caption text-grey" }, "CPU", -1)),
                            _createElementVNode("div", null, _toDisplayString(equipmentToClone.value?.cpu || 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, { cols: "6" }, {
                          default: _withCtx(() => [
                            _cache[41] || (_cache[41] = _createElementVNode("div", { class: "text-caption text-grey" }, "RAM", -1)),
                            _createElementVNode("div", null, _toDisplayString(equipmentToClone.value?.ram || 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, { cols: "6" }, {
                          default: _withCtx(() => [
                            _cache[42] || (_cache[42] = _createElementVNode("div", { class: "text-caption text-grey" }, "Storage", -1)),
                            _createElementVNode("div", null, _toDisplayString(equipmentToClone.value?.storage || 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, { cols: "6" }, {
                          default: _withCtx(() => [
                            _cache[43] || (_cache[43] = _createElementVNode("div", { class: "text-caption text-grey" }, "GPU", -1)),
                            _createElementVNode("div", null, _toDisplayString(equipmentToClone.value?.gpu || 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, { cols: "6" }, {
                          default: _withCtx(() => [
                            _cache[44] || (_cache[44] = _createElementVNode("div", { class: "text-caption text-grey" }, "OS", -1)),
                            _createElementVNode("div", null, _toDisplayString(equipmentToClone.value?.os || 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, { cols: "6" }, {
                          default: _withCtx(() => [
                            _cache[45] || (_cache[45] = _createElementVNode("div", { class: "text-caption text-grey" }, "Screen", -1)),
                            _createElementVNode("div", null, _toDisplayString(equipmentToClone.value?.screen || 'N/A'), 1)
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                (addMoreError.value)
                  ? (_openBlock(), _createBlock(_component_v_alert, {
                      key: 1,
                      type: "error",
                      variant: "tonal",
                      class: "mt-3"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(addMoreError.value), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  onClick: closeAddMoreModal
                }, {
                  default: _withCtx(() => [...(_cache[46] || (_cache[46] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "primary",
                  onClick: confirmAddMore,
                  disabled: !addMoreQuantity.value || addMoreQuantity.value < 1 || addMoreSubmitting.value,
                  loading: addMoreSubmitting.value
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Add " + _toDisplayString(addMoreQuantity.value || 0) + " Item(s) ", 1)
                  ]),
                  _: 1
                }, 8, ["disabled", "loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})